import { Alpha3Code } from 'i18n-iso-countries'
import { ReactNode } from 'react'

import {
  Avatar,
  FlagAvatar,
  Flex,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TablePagination,
  TableProvider,
  TableRow,
  TableSearchAndFilters,
  Tooltip,
} from '@percent/lemonade'

import { useOrganisationProfileContext } from '../../context/OrganisationProfileContext'
import { useTranslation } from '../../hooks/useTranslation'
import { ProfileLink } from '../ProfileLink/ProfileLink'
import { OrganisationProfileSummary } from '../../types/organisationProfile.types'

export type OrganisationTableProps = {
  rows: OrganisationProfileSummary[]
  totalResults: number | null
  // False when totalResults is only a lower bound, shown as "N+ results".
  exhaustiveTotalResults?: boolean
  isLoading: boolean
  emptyState: { title: string; description?: string }
  page: number
  pageSize: number
  onPageChange: (page: number) => void
  // Partner-only column, relative to the viewing partner's donations.
  showLastEngagement?: boolean
  // Rendered above the table, inside the same card.
  filters?: ReactNode
}

export function OrganisationTable({
  rows,
  totalResults,
  exhaustiveTotalResults = true,
  isLoading,
  emptyState,
  page,
  pageSize,
  onPageChange,
  showLastEngagement = false,
  filters,
}: Readonly<OrganisationTableProps>) {
  const { t } = useTranslation()
  const { countries, locale, links } = useOrganisationProfileContext()

  const lastPage = Math.max(1, Math.ceil((totalResults ?? 0) / pageSize))
  const getCountryName = (code: string) =>
    countries.find((country) => country.code === code)?.name ?? code

  const columns: TableColumn[] = [
    {
      id: 'country',
      content: t('organisations.table.country'),
      props: { width: '7%' },
    },
    {
      id: 'organisation',
      content: t('organisations.table.organisation'),
      props: { width: showLastEngagement ? '53%' : '73%' },
    },
    {
      id: 'registry',
      content: t('organisations.table.registry'),
      props: { width: '20%' },
    },
    ...(showLastEngagement
      ? [
          {
            id: 'lastDonation',
            content: t('organisations.table.lastDonation'),
            props: { width: '20%' },
          },
        ]
      : []),
  ]

  return (
    <TableProvider
      columns={columns}
      totalResults={totalResults}
      isLoading={isLoading}
      illustration="no-results"
      emptyTableTitle={emptyState.title}
      emptyTableDescription={emptyState.description}
    >
      {filters && <TableSearchAndFilters>{filters}</TableSearchAndFilters>}
      <Table>
        <TableBody>
          {rows.map(({ organisation, lastEngagementAt }) => {
            const organisationName =
              organisation.displayName || organisation.name

            return (
              <TableRow key={organisation.id}>
                <TableCell>
                  <Tooltip content={getCountryName(organisation.countryCode)}>
                    <div>
                      <FlagAvatar
                        code3={organisation.countryCode as Alpha3Code}
                        width="28px"
                      />
                    </div>
                  </Tooltip>
                </TableCell>
                <TableCell>
                  <Flex align="center" gap={8}>
                    <Avatar
                      image={organisation.logo}
                      alt={organisationName}
                      size="small"
                    />
                    {links.organisation ? (
                      <ProfileLink
                        href={links.organisation(organisation.id)}
                        fz="inherit"
                      >
                        {organisationName}
                      </ProfileLink>
                    ) : (
                      organisationName
                    )}
                  </Flex>
                </TableCell>
                <TableCell>
                  {organisation.registry?.registryId ?? '—'}
                </TableCell>
                {showLastEngagement && (
                  <TableCell>
                    {lastEngagementAt
                      ? new Date(lastEngagementAt).toLocaleDateString(locale, {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        })
                      : '—'}
                  </TableCell>
                )}
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
      <Flex direction="column" p="10px">
        <TablePagination
          totalResultsText={
            exhaustiveTotalResults
              ? t('organisations.table.results', { count: totalResults ?? 0 })
              : // Raw number: TablePagination formats digits itself, and would split "10,000".
                t('organisations.table.resultsCapped', {
                  total: totalResults ?? 0,
                })
          }
          previousPage={page > 1 ? () => onPageChange(page - 1) : null}
          nextPage={page < lastPage ? () => onPageChange(page + 1) : null}
        />
      </Flex>
    </TableProvider>
  )
}
